import { zodResolver } from "@hookform/resolvers/zod";
import { ColumnDef } from "@tanstack/react-table";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Check, Copy, ExternalLink, Eye, Loader2, Pencil, RefreshCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "wouter";
import { z } from "zod";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  getOuraOperationsOptions,
  getOuraProjectCollectionDetailOptions,
  getOuraProjectDetailOptions,
  ouraMutationOptions,
} from "@/services/queryOptions/ouraQueryOptions";
import {
  buildProjectPayload,
  OuraListParams,
  OuraOnchainProject,
  OuraOperation,
  OuraProject,
  OuraProjectCollectionDetail,
  OuraProjectCollection,
  OuraProjectDetail,
  OuraTransactionResult,
  projectSchema,
} from "@/types/oura";
import { getSolscanAccountUrl, getSolscanTxUrl } from "@/utils/solscan";
import { AdminDataTable } from "../components/AdminDataTable";
import { SecurityNote, TextAreaField, TextField } from "../components/FormControls";

type ProjectFormValues = z.infer<typeof projectSchema>;
type ProjectTransactionSuccess = {
  mode: "create" | "update";
  result: OuraTransactionResult;
  values: ProjectFormValues;
};

const defaults: ProjectFormValues = {
  admin: "",
  collection: "",
  token_mint: "",
  max_supply: "0",
  released: "0",
  max_mint_per_user: "0",
  max_mint_per_vip_user: "0",
  vesting_mode: "None",
  vesting_unlock_ts: "0",
  mint_nft_fee_lamports: "0",
  update_nft_fee_lamports: "0",
  mint_price_total: "0",
  escrow_amount: "0",
  revenue_wallets: "",
  revenue_shares: "",
  royalty_recipients: "",
  royalty_shares_bps: "",
  collection_name: "",
  collection_uri: "",
};

const vestingModes = ["None", "Permanent", "Switch", "TimeStamp"] as const;

function short(value?: string | null) {
  if (!value) return "-";
  return `${value.slice(0, 6)}...${value.slice(-4)}`;
}

function csv(values?: Array<string | number> | null) {
  return values?.map(String).join(", ") ?? "";
}

function namesPreview(values: string[]) {
  if (!values.length) return "-";
  const visible = values.slice(0, 3).join(", ");
  return values.length > 3 ? `${visible} +${values.length - 3}` : visible;
}

function normalizeVestingMode(value?: string | null): ProjectFormValues["vesting_mode"] {
  if (value && vestingModes.includes(value as ProjectFormValues["vesting_mode"])) {
    return value as ProjectFormValues["vesting_mode"];
  }

  const byIndex = vestingModes[Number(value)];
  return byIndex ?? "None";
}

function TransactionDetailRow({
  label,
  value,
  copied,
  onCopy,
  href,
}: {
  label: string;
  value?: string | null;
  copied: boolean;
  onCopy: (value: string, label: string) => void;
  href?: string | null;
}) {
  const displayValue = value || "-";
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex items-start gap-2 rounded-md border border-zinc-800 bg-black/40 p-3">
        <span className="min-w-0 flex-1 break-all font-mono text-xs leading-5">
          {displayValue}
        </span>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-zinc-400 hover:text-white"
            onClick={() => onCopy(value, label)}
            aria-label={`Copy ${label}`}
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
          </Button>
        )}
        {href && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-zinc-400 hover:text-white"
            asChild
          >
            <a href={href} target="_blank" rel="noreferrer" aria-label={`Open ${label} in Solscan`}>
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}

function ProjectTransactionSuccessDialog({
  detail,
  onOpenChange,
}: {
  detail: ProjectTransactionSuccess | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const title = detail?.mode === "create" ? "Project created" : "Project updated";
  const collection = detail?.result.collection ?? detail?.values.collection;
  const signature = detail?.result.signature;
  const tokenMint = detail?.values.token_mint;

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast({ title: "Copied", description: `${label} copied to clipboard` });
      window.setTimeout(() => setCopiedField((current) => (current === label ? null : current)), 1500);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: error instanceof Error ? error.message : `Could not copy ${label}`,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={Boolean(detail)}
      onOpenChange={(open) => {
        if (!open) setCopiedField(null);
        onOpenChange(open);
      }}
    >
      <DialogContent className="w-[90%] max-w-lg border border-gray-800 bg-secondary text-white">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="text-gray-300">
            The on-chain transaction was submitted successfully.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <TransactionDetailRow
            label="Collection address"
            value={collection}
            copied={copiedField === "Collection address"}
            onCopy={copyValue}
          />
          <TransactionDetailRow
            label="Transaction signature"
            value={signature}
            copied={copiedField === "Transaction signature"}
            onCopy={copyValue}
          />
          <TransactionDetailRow
            label="Token mint"
            value={tokenMint}
            copied={copiedField === "Token mint"}
            onCopy={copyValue}
          />
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function summaryString(summary: Record<string, unknown> | null | undefined, key: string) {
  const value = summary?.[key];
  if (value === null || value === undefined) return "";
  if (Array.isArray(value)) return value.map(String).join(", ");
  return String(value);
}

function summaryJson(summary: Record<string, unknown> | null | undefined, redactAdmin = false) {
  if (!summary) return "{}";
  const sanitized = Object.fromEntries(
    Object.entries(summary).filter(([key]) => !(redactAdmin && key === "admin")),
  );
  return JSON.stringify(sanitized, null, 2);
}

function valuesFromOperation(operation?: OuraOperation | null): ProjectFormValues {
  if (!operation) return defaults;
  const summary = operation.requestSummary;

  return {
    ...defaults,
    admin: "",
    collection: operation.collection ?? summaryString(summary, "collection"),
    token_mint: operation.tokenMint ?? summaryString(summary, "token_mint"),
    max_supply: summaryString(summary, "max_supply") || "0",
    released: summaryString(summary, "released") || "0",
    max_mint_per_user: summaryString(summary, "max_mint_per_user") || "0",
    max_mint_per_vip_user: summaryString(summary, "max_mint_per_vip_user") || "0",
    vesting_mode: normalizeVestingMode(summaryString(summary, "vesting_mode")),
    vesting_unlock_ts: summaryString(summary, "vesting_unlock_ts") || "0",
    mint_nft_fee_lamports: summaryString(summary, "mint_nft_fee_lamports") || "0",
    update_nft_fee_lamports: summaryString(summary, "update_nft_fee_lamports") || "0",
    mint_price_total: summaryString(summary, "mint_price_total") || "0",
    escrow_amount: summaryString(summary, "escrow_amount") || "0",
    revenue_wallets: summaryString(summary, "revenue_wallets"),
    revenue_shares: summaryString(summary, "revenue_shares"),
    royalty_recipients: summaryString(summary, "royalty_recipients"),
    royalty_shares_bps: summaryString(summary, "royalty_shares_bps"),
    collection_name: summaryString(summary, "collection_name"),
    collection_uri: summaryString(summary, "collection_uri"),
  };
}

function OperationDetailDialog({
  operation,
  onOpenChange,
}: {
  operation: OuraOperation | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      toast({ title: "Copied", description: `${label} copied to clipboard` });
      window.setTimeout(() => setCopiedField((current) => (current === label ? null : current)), 1500);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: error instanceof Error ? error.message : `Could not copy ${label}`,
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog
      open={Boolean(operation)}
      onOpenChange={(open) => {
        if (!open) setCopiedField(null);
        onOpenChange(open);
      }}
    >
      <DialogContent className="w-[92%] max-w-3xl border border-gray-800 bg-secondary text-white">
        <DialogHeader>
          <DialogTitle>Project Transaction Detail</DialogTitle>
          <DialogDescription className="text-gray-300">
            Created {operation ? new Date(operation.createdAt).toLocaleString() : "-"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <TransactionDetailRow
              label="Transaction signature"
              value={operation?.signature}
              copied={copiedField === "Transaction signature"}
              onCopy={copyValue}
              href={operation?.signature ? getSolscanTxUrl(operation.signature) : null}
            />
            <TransactionDetailRow
              label="Collection address"
              value={operation?.collection}
              copied={copiedField === "Collection address"}
              onCopy={copyValue}
              href={operation?.collection ? getSolscanAccountUrl(operation.collection) : null}
            />
            <TransactionDetailRow
              label="Token mint"
              value={operation?.tokenMint}
              copied={copiedField === "Token mint"}
              onCopy={copyValue}
              href={operation?.tokenMint ? getSolscanAccountUrl(operation.tokenMint) : null}
            />
            <TransactionDetailRow
              label="Asset"
              value={operation?.asset}
              copied={copiedField === "Asset"}
              onCopy={copyValue}
              href={operation?.asset ? getSolscanAccountUrl(operation.asset) : null}
            />
            <TransactionDetailRow
              label="Vault ATA"
              value={operation?.vaultAta}
              copied={copiedField === "Vault ATA"}
              onCopy={copyValue}
              href={operation?.vaultAta ? getSolscanAccountUrl(operation.vaultAta) : null}
            />
            <DetailField label="Status" value={operation?.status ? <Badge>{operation.status}</Badge> : "-"} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Request summary</div>
              <pre className="max-h-72 overflow-auto rounded-md border border-zinc-800 bg-black/40 p-3 text-xs">
                {summaryJson(operation?.requestSummary, true)}
              </pre>
            </div>
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Response summary</div>
              <pre className="max-h-72 overflow-auto rounded-md border border-zinc-800 bg-black/40 p-3 text-xs">
                {summaryJson(operation?.responseSummary)}
              </pre>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function valuesFromDetail(detail?: OuraProjectCollectionDetail): ProjectFormValues {
  if (!detail) return defaults;

  const project = detail.project;
  const onchain = detail.onchain;

  return {
    ...defaults,
    collection: project.resolvedCollection ?? "",
    token_mint: project.resolvedTokenMint ?? onchain?.token_mint ?? "",
    max_supply: onchain?.max_supply ?? project.ouraMaxSupply ?? "0",
    released: onchain?.released ?? project.ouraReleased ?? "0",
    max_mint_per_user: onchain?.max_mint_per_user ?? "0",
    max_mint_per_vip_user: onchain?.max_mint_per_vip_user ?? "0",
    vesting_mode: normalizeVestingMode(onchain?.vesting_mode),
    vesting_unlock_ts: onchain?.vesting_unlock_ts ?? "0",
    mint_nft_fee_lamports: onchain?.mint_nft_fee_lamports ?? "0",
    update_nft_fee_lamports: onchain?.update_nft_fee_lamports ?? "0",
    mint_price_total: onchain?.mint_price_total ?? "0",
    escrow_amount: onchain?.escrow_amount ?? "0",
    revenue_wallets: csv(onchain?.revenue_wallets),
    revenue_shares: csv(onchain?.revenue_shares),
    royalty_recipients: csv(project.ouraRoyaltyRecipients),
    royalty_shares_bps: csv(project.ouraRoyaltySharesBps),
    collection_name: project.ouraCollectionName ?? "",
    collection_uri: project.ouraCollectionUri ?? "",
  };
}

function ProjectForm({
  mode,
  initialValues,
  disabled = false,
  disabledReason,
  onSuccess,
  onTransactionSuccess,
}: {
  mode: "create" | "update";
  initialValues?: ProjectFormValues;
  disabled?: boolean;
  disabledReason?: string | null;
  onSuccess?: () => void;
  onTransactionSuccess?: (result: OuraTransactionResult, values: ProjectFormValues) => void;
}) {
  const { toast } = useToast();
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: initialValues ?? defaults,
  });
  const mutation = useMutation(
    ouraMutationOptions<Record<string, unknown>>(
      mode === "create" ? "POST" : "PUT",
      "/api/v1/oura/project",
    ),
  );

  useEffect(() => {
    form.reset(initialValues ?? defaults);
  }, [form, initialValues]);

  const submit = form.handleSubmit((values) => {
    const payload = buildProjectPayload(values);
    if (mode === "update" && !payload.collection) {
      form.setError("collection", { message: "Collection is required for updates" });
      return;
    }
    if (payload.num_revenue_wallets !== payload.revenue_shares.length) {
      form.setError("revenue_shares", { message: "Revenue shares must match wallet count" });
      return;
    }
    if (payload.num_royalty_recipients !== payload.royalty_shares_bps.length) {
      form.setError("royalty_shares_bps", { message: "Royalty shares must match recipient count" });
      return;
    }
    const body = mode === "create" ? { ...payload, collection: undefined } : payload;
    mutation.mutate(body, {
      onSuccess: (result) => {
        onTransactionSuccess?.(result, values);
        if (mode === "create") form.reset(defaults);
        if (mode === "update") form.reset({ ...values, admin: "" });
        onSuccess?.();
      },
      onError: (error) => {
        toast({
          title: "Project transaction failed",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>{mode === "create" ? "Create Project" : "Edit On-chain Project"}</CardTitle>
      </CardHeader>
      <CardContent>
        {disabledReason && (
          <div className="mb-4 flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{disabledReason}</span>
          </div>
        )}
        <form className="grid gap-4 lg:grid-cols-2" onSubmit={submit}>
          <div className="lg:col-span-2">
            <SecurityNote />
          </div>
          <TextAreaField
            label="Admin secretKey JSON"
            placeholder="[12,34,56,...]"
            registration={form.register("admin")}
            error={form.formState.errors.admin}
          />
          {mode === "update" && (
            <TextField
              label="Collection"
              placeholder="So11111111111111111111111111111111111111112"
              registration={form.register("collection")}
              error={form.formState.errors.collection}
            />
          )}
          <TextField
            label="Token mint"
            placeholder="So11111111111111111111111111111111111111112"
            registration={form.register("token_mint")}
            error={form.formState.errors.token_mint}
          />
          <TextField label="Max supply" placeholder="1000" registration={form.register("max_supply")} error={form.formState.errors.max_supply} />
          <TextField label="Released" placeholder="0" registration={form.register("released")} error={form.formState.errors.released} />
          <TextField label="Max mint per user" placeholder="1" registration={form.register("max_mint_per_user")} error={form.formState.errors.max_mint_per_user} />
          <TextField label="Max mint per VIP" placeholder="3" registration={form.register("max_mint_per_vip_user")} error={form.formState.errors.max_mint_per_vip_user} />
          <div className="space-y-2">
            <label className="text-sm font-medium">Vesting mode</label>
            <select className="h-10 w-full rounded border border-input bg-background px-3 text-sm" disabled={disabled || mutation.isPending} {...form.register("vesting_mode")}>
              {vestingModes.map((mode) => (
                <option key={mode} value={mode}>{mode}</option>
              ))}
            </select>
          </div>
          <TextField
            label="Vesting unlock timestamp"
            placeholder="1717430400"
            registration={form.register("vesting_unlock_ts")}
            error={form.formState.errors.vesting_unlock_ts}
          />
          <TextField
            label="Mint NFT fee lamports"
            placeholder="5000000"
            registration={form.register("mint_nft_fee_lamports")}
            error={form.formState.errors.mint_nft_fee_lamports}
          />
          <TextField
            label="Update NFT fee lamports"
            placeholder="2000000"
            registration={form.register("update_nft_fee_lamports")}
            error={form.formState.errors.update_nft_fee_lamports}
          />
          <TextField label="Mint price total" placeholder="10000000" registration={form.register("mint_price_total")} error={form.formState.errors.mint_price_total} />
          <TextField label="Escrow amount" placeholder="2500000" registration={form.register("escrow_amount")} error={form.formState.errors.escrow_amount} />
          <TextField
            label="Revenue wallets CSV"
            placeholder="So11111111111111111111111111111111111111112,9xQeWvG816bUx9EPwWQqY1yYbP8m9m9m9m9m9m9m9m9"
            registration={form.register("revenue_wallets")}
            error={form.formState.errors.revenue_wallets}
          />
          <TextField label="Revenue shares CSV" placeholder="70,30" registration={form.register("revenue_shares")} error={form.formState.errors.revenue_shares} />
          <TextField
            label="Royalty recipients CSV"
            placeholder="So11111111111111111111111111111111111111112,9xQeWvG816bUx9EPwWQqY1yYbP8m9m9m9m9m9m9m9m9"
            registration={form.register("royalty_recipients")}
            error={form.formState.errors.royalty_recipients}
          />
          <TextField label="Royalty shares BPS CSV" placeholder="500,500" registration={form.register("royalty_shares_bps")} error={form.formState.errors.royalty_shares_bps} />
          <TextField label="Collection name" placeholder="Tweeto Genesis" registration={form.register("collection_name")} error={form.formState.errors.collection_name} />
          <TextField
            label="Collection URI"
            placeholder="https://example.com/collection.json"
            registration={form.register("collection_uri")}
            error={form.formState.errors.collection_uri}
          />
          <Button className="lg:col-span-2" disabled={disabled || mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "create" ? "Create project" : "Update on-chain project"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function DetailField({ label, value, mono = false }: { label: string; value: React.ReactNode; mono?: boolean }) {
  const displayValue = value === null || value === undefined || value === "" ? "-" : value;
  return (
    <div className="min-w-0">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={mono ? "break-all font-mono text-xs" : "break-words text-sm"}>{displayValue}</div>
    </div>
  );
}

function ProjectSummary({ project }: { project: OuraProject }) {
  return (
    <div className="space-y-6">
      <section>
        <h3 className="mb-3 border-b pb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Project</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailField label="Project ID" value={project.projectId} mono />
          <DetailField label="Title" value={project.projectTitle} />
          <DetailField label="Start Date" value={new Date(project.projectStartDate).toLocaleString()} />
          <DetailField label="End Date" value={new Date(project.projectEndDate).toLocaleString()} />
          <DetailField label="Active" value={<Badge variant={project.projectIsActive ? "default" : "secondary"}>{project.projectIsActive ? "Yes" : "No"}</Badge>} />
          <DetailField label="VIP Session" value={<Badge variant={project.projectIsVipSession ? "default" : "secondary"}>{project.projectIsVipSession ? "Yes" : "No"}</Badge>} />
          <DetailField label="Egg Mechanism" value={<Badge variant={project.projectIsEggMechanism ? "default" : "secondary"}>{project.projectIsEggMechanism ? "Yes" : "No"}</Badge>} />
          <DetailField label="Description" value={project.projectDescription} />
        </div>
      </section>

      <section>
        <h3 className="mb-3 border-b pb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Partner</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailField label="Partner ID" value={project.partnerId} mono />
          <DetailField label="Name" value={project.partnerName} />
          <DetailField label="Slug" value={project.partnerSlug} mono />
        </div>
      </section>

      <section>
        <h3 className="mb-3 border-b pb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Assets</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailField label="NFT Collection" value={project.projectNftCollection} mono />
          <DetailField label="Token Mint" value={project.tokenMint} mono />
          <DetailField label="NFT Count" value={<Badge variant="outline">{project.nftCount}</Badge>} />
          <DetailField label="Trait Count" value={<Badge variant="outline">{project.traitCount}</Badge>} />
          <DetailField label="Egg" value={project.eggName ?? "-"} />
          <DetailField label="Total Max Minted" value={project.totalMaxMinted} />
        </div>
      </section>
    </div>
  );
}

function OnchainSummary({
  detail,
  onRefresh,
  isFetching,
}: {
  detail: OuraProjectCollectionDetail;
  onRefresh: () => void;
  isFetching: boolean;
}) {
  const project = detail.project;
  const onchain: OuraOnchainProject | null = detail.onchain;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>On-chain Config</CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={isFetching}>
          <RefreshCcw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {detail.onchain_error && (
          <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{detail.onchain_error}</span>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <DetailField label="Resolved Collection" value={project.resolvedCollection} mono />
          <DetailField label="Resolved Token Mint" value={project.resolvedTokenMint} mono />
          <DetailField label="Admin" value={onchain?.admin ?? project.ouraAdmin} mono />
          <DetailField label="Max Supply" value={onchain?.max_supply ?? project.ouraMaxSupply} />
          <DetailField label="Released" value={onchain?.released ?? project.ouraReleased} />
          <DetailField label="Admin Minted" value={onchain?.admin_minted ?? project.ouraAdminMinted} />
          <DetailField label="User Minted" value={onchain?.user_minted ?? project.ouraUserMinted} />
          <DetailField label="Collection Name" value={project.ouraCollectionName} />
          <DetailField label="Last Signature" value={project.ouraLastSignature} mono />
          <DetailField label="Indexed Updated At" value={project.ouraUpdatedAt ? new Date(project.ouraUpdatedAt).toLocaleString() : "-"} />
        </div>
      </CardContent>
    </Card>
  );
}

function CollectionSelector({
  collections,
  selectedCollection,
  onSelect,
}: {
  collections: OuraProjectCollection[];
  selectedCollection: string | null;
  onSelect: (collection: string) => void;
}) {
  if (!collections.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>NFT Collections</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          No NFT collections are configured for this project.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>NFT Collections</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded border border-zinc-800">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Collection</TableHead>
                <TableHead>NFT Names</TableHead>
                <TableHead>Eggs</TableHead>
                <TableHead>Max Minted</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collections.map((item) => {
                const active = item.collection === selectedCollection;
                return (
                  <TableRow
                    key={item.collection}
                    className={active ? "bg-primary/10" : undefined}
                  >
                    <TableCell className="font-medium">
                      {item.collectionName ?? "-"}
                    </TableCell>
                    <TableCell className="break-all font-mono text-xs">
                      {item.collection}
                    </TableCell>
                    <TableCell className="max-w-[260px] text-sm text-zinc-300">
                      {namesPreview(item.nftNames)}
                    </TableCell>
                    <TableCell className="max-w-[220px] text-sm text-zinc-300">
                      {namesPreview(item.eggNames)}
                    </TableCell>
                    <TableCell>{item.totalMaxMinted}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onSelect(item.collection)}
                        aria-label="View collection on-chain details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ProjectDetailDrawer({
  projectId,
  collection,
  open,
  onOpenChange,
  onTransactionSuccess,
}: {
  projectId: number | null;
  collection: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTransactionSuccess?: (detail: ProjectTransactionSuccess) => void;
}) {
  const detailQuery = useQuery(getOuraProjectCollectionDetailOptions(projectId, collection));
  const detail = detailQuery.data;
  const initialValues = useMemo(() => valuesFromDetail(detail), [detail]);
  const disabledReason = detail
    ? detail.onchain_error ||
      (!detail.project.ouraRoyaltyRecipients?.length || !detail.project.ouraRoyaltySharesBps?.length
        ? "Royalty configuration is not indexed yet. Add royalty recipients and shares before submitting an update."
        : null)
    : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
        <SheetHeader className="border-b px-6 py-5">
          <SheetTitle>{collection ? short(collection) : "NFT Collection"}</SheetTitle>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-73px)]">
          <div className="space-y-6 p-6">
            {detailQuery.isLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading project detail
              </div>
            )}
            {detailQuery.error && (
              <div className="flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{detailQuery.error.message}</span>
              </div>
            )}
            {detail && (
              <>
                <OnchainSummary
                  detail={detail}
                  onRefresh={() => detailQuery.refetch()}
                  isFetching={detailQuery.isFetching}
                />
                <ProjectForm
                  mode="update"
                  initialValues={initialValues}
                  disabled={!detail.onchain || Boolean(detail.onchain_error)}
                  disabledReason={disabledReason}
                  onSuccess={() => detailQuery.refetch()}
                  onTransactionSuccess={(result, values) =>
                    onTransactionSuccess?.({ mode: "update", result, values })
                  }
                />
              </>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

export function ProjectDetailPage({ projectId }: { projectId: number }) {
  const [, setLocation] = useLocation();
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [transactionSuccess, setTransactionSuccess] = useState<ProjectTransactionSuccess | null>(null);
  const query = useQuery(getOuraProjectDetailOptions(projectId));
  const project = query.data?.project;
  const collections = project?.nftCollectionDetails ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => setLocation("/tweeto-admin/oura/projects")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Projects
        </Button>
        <div>
          <h2 className="text-2xl font-semibold">{project?.projectTitle ?? "Project Details"}</h2>
          <p className="text-sm text-zinc-400">Project metadata and NFT collections. Select a collection to inspect on-chain config.</p>
        </div>
      </div>

      {query.isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading project detail
        </div>
      )}
      {query.error && (
        <div className="flex gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{query.error.message}</span>
        </div>
      )}
      {project && (
        <>
          <ProjectSummary project={project} />
          <CollectionSelector
            collections={collections}
            selectedCollection={selectedCollection}
            onSelect={setSelectedCollection}
          />
        </>
      )}

      <ProjectDetailDrawer
        projectId={projectId}
        collection={selectedCollection}
        open={selectedCollection !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedCollection(null);
        }}
        onTransactionSuccess={setTransactionSuccess}
      />
      <ProjectTransactionSuccessDialog
        detail={transactionSuccess}
        onOpenChange={(open) => {
          if (!open) setTransactionSuccess(null);
        }}
      />
    </div>
  );
}

export function ProjectManager() {
  const [params, setParams] = useState<OuraListParams>({
    page: 1,
    page_size: 10,
    resource_type: "project",
    action_type: "initialize",
  });
  const [isCreateProjectOpen, setIsCreateProjectOpen] = useState(false);
  const [editingOperation, setEditingOperation] = useState<OuraOperation | null>(null);
  const [viewingOperation, setViewingOperation] = useState<OuraOperation | null>(null);
  const [transactionSuccess, setTransactionSuccess] = useState<ProjectTransactionSuccess | null>(null);
  const query = useQuery(getOuraOperationsOptions(params));
  const editInitialValues = useMemo(() => valuesFromOperation(editingOperation), [editingOperation]);

  const columns = useMemo<ColumnDef<OuraOperation>[]>(
    () => [
      {
        accessorKey: "createdAt",
        header: "Time",
        cell: ({ row }) => new Date(row.original.createdAt).toLocaleString(),
      },
      {
        accessorKey: "resourceType",
        header: "Resource",
        cell: ({ row }) => <Badge variant="outline">{row.original.resourceType}</Badge>,
      },
      { accessorKey: "actionType", header: "Action" },
      {
        accessorKey: "signature",
        header: "Signature",
        cell: ({ row }) =>
          row.original.signature ? (
            <a
              className="font-mono text-cyan-300 hover:underline"
              href={getSolscanTxUrl(row.original.signature)}
              target="_blank"
              rel="noreferrer"
            >
              {short(row.original.signature)}
            </a>
          ) : (
            "-"
          ),
      },
      {
        accessorKey: "collection",
        header: "Collection",
        cell: ({ row }) => <span className="font-mono">{short(row.original.collection)}</span>,
      },
      {
        accessorKey: "asset",
        header: "Asset",
        cell: ({ row }) => <span className="font-mono">{short(row.original.asset)}</span>,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => <Badge>{row.original.status}</Badge>,
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => (
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setViewingOperation(row.original)}
              aria-label="View project transaction detail"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setEditingOperation(row.original)}
              aria-label="Edit project"
            >
              <Pencil className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Projects</h2>
        <p className="text-sm text-zinc-400">Project initialization history. Inspect transactions, copy addresses, or edit an initialized project.</p>
      </div>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle>Project Creation History</CardTitle>
          <Button onClick={() => setIsCreateProjectOpen(true)}>Add Project</Button>
        </CardHeader>
        <CardContent>
          <AdminDataTable
            columns={columns}
            data={query.data?.items ?? []}
            total={query.data?.total ?? 0}
            params={params}
            onParamsChange={(next) =>
              setParams({
                ...next,
                resource_type: "project",
                action_type: "initialize",
              })
            }
            exportName="oura-projects.csv"
          />
        </CardContent>
      </Card>
      <Sheet open={isCreateProjectOpen} onOpenChange={setIsCreateProjectOpen}>
        <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
          <SheetHeader className="border-b px-6 py-5">
            <SheetTitle>Add Project</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-73px)]">
            <div className="p-6">
              <ProjectForm
                mode="create"
                onSuccess={() => setIsCreateProjectOpen(false)}
                onTransactionSuccess={(result, values) =>
                  setTransactionSuccess({ mode: "create", result, values })
                }
              />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <Sheet open={Boolean(editingOperation)} onOpenChange={(open) => !open && setEditingOperation(null)}>
        <SheetContent side="right" className="w-full overflow-hidden p-0 sm:max-w-3xl">
          <SheetHeader className="border-b px-6 py-5">
            <SheetTitle>Edit Project {editingOperation?.collection ? short(editingOperation.collection) : ""}</SheetTitle>
          </SheetHeader>
          <ScrollArea className="h-[calc(100vh-73px)]">
            <div className="p-6">
              <ProjectForm
                mode="update"
                initialValues={editInitialValues}
                onSuccess={() => setEditingOperation(null)}
                onTransactionSuccess={(result, values) =>
                  setTransactionSuccess({ mode: "update", result, values })
                }
              />
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>
      <OperationDetailDialog
        operation={viewingOperation}
        onOpenChange={(open) => {
          if (!open) setViewingOperation(null);
        }}
      />
      <ProjectTransactionSuccessDialog
        detail={transactionSuccess}
        onOpenChange={(open) => {
          if (!open) setTransactionSuccess(null);
        }}
      />
    </div>
  );
}
